import { config, useTransition } from "@react-spring/web";

import { HaNBookingDetailsProps } from "./types";

import { HaNModalHeader } from "../HaNModalHeader";
import { BookingDetailsWrapper } from "./styles";
import { useTranslation } from "react-i18next";
import { MeetingRoomBookingDetailsContent, ModalPageBottom } from "@organisms";
import { useCreateEvent, useDestructiveDialog, useFormatDate } from "@hooks";
import {
  useAppDispatch,
  useAppSelector,
  markAsBookedLocally,
  getIsOnlineMeetingLinkSupported,
  getMeetingRoomDynamicFreeTimeslot,
} from "@lib/store";
import { isSameDay, parseISO } from "date-fns";
import { useState, useCallback, useEffect } from "react";
import { FlexCol } from "@quarks";
import { useActiveMeetingRoomRequest } from "@pages";

export const HaNBookingDetails = ({
  onBack,
  onClose,
  page,
  room,
  duration,
  startDateTime: initialStartDateTime,
  prevPage,
  resetAttendees,
  setPage,
  setHasUnsavedChanges,
  attendees,
  onBookSuccessCallback,
}: HaNBookingDetailsProps) => {
  const { t } = useTranslation();
  const transition = useTransition(page === "bookingDetails", {
    from: { transform: prevPage === "addInvitees" ? "translate3d(-100%, 0, 0)" : "translate3d(100%, 0, 0)" },
    enter: { transform: "translate3d(0%, 0, 0)" },
    leave: { transform: page === "addInvitees" ? "translate3d(-100%, 0, 0)" : "translate3d(100%, 0, 0)" },
    config: { ...config.stiff, clamp: true },
  });

  const onlineMeetingLinkSupported = useAppSelector(getIsOnlineMeetingLinkSupported);
  const email = "data" in room ? (room.data.email ?? "") : room.email;

  const displayName = "data" in room ? room.name : room.displayName;
  const [title, setTitle] = useState<string>(displayName);
  const [message, setMessage] = useState<string>("");
  const [isOnlineMeeting, setIsOnlineMeeting] = useState<boolean>(onlineMeetingLinkSupported);
  const dispatch = useAppDispatch();
  const { destructiveDialog } = useDestructiveDialog();

  const activeMeetingRoomRequest = useActiveMeetingRoomRequest();
  const dynamicTimeslot = useAppSelector(state =>
    getMeetingRoomDynamicFreeTimeslot(state, activeMeetingRoomRequest, room.id, initialStartDateTime, {
      minutes: duration,
    }),
  );

  const startDateTime = dynamicTimeslot?.start.toISOString() ?? "";
  const endDateTime = dynamicTimeslot?.end.toISOString() ?? "";

  const endDateFormat = isSameDay(parseISO(startDateTime), parseISO(endDateTime)) ? "HH:mm" : "dd MMMM HH:mm";

  const confirmationMessageStartDateTime = useFormatDate(startDateTime, "dd MMMM HH:mm");
  const confirmationMessageEndDateTime = useFormatDate(endDateTime, endDateFormat);

  const handleOnSuccess = () => {
    dispatch(markAsBookedLocally({ buildingId: room.buildingId, roomId: room.id }));
    onBookSuccessCallback && onBookSuccessCallback();
    onClose();
  };

  const { createEvent, isCreatingEvent } = useCreateEvent(handleOnSuccess);

  const hasUnsavedChanges =
    title !== displayName || message !== "" || attendees.length > 0 || (onlineMeetingLinkSupported && !isOnlineMeeting);

  const handleClose = useCallback(async () => {
    if (!hasUnsavedChanges) {
      onClose();
    } else {
      const confirmation = await destructiveDialog(
        t("meeting:DiscardEventChangesTitle"),
        t("meeting:DiscardEventChangesMessage"),
        "warning",
        t("meeting:DiscardEventChangesConfirm"),
      );
      if (confirmation) onClose();
    }
  }, [destructiveDialog, t, hasUnsavedChanges, onClose]);

  useEffect(() => {
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(!!hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  const handleBackClick = useCallback(async () => {
    if (!hasUnsavedChanges) {
      onBack();
    } else {
      const confirmation = await destructiveDialog(
        t("meeting:DiscardEventChangesTitle"),
        t("meeting:DiscardEventChangesMessage"),
        "warning",
        t("meeting:DiscardEventChangesConfirm"),
      );
      if (confirmation) {
        setMessage("");
        setTitle(displayName);
        setIsOnlineMeeting(true);
        resetAttendees();
        onBack();
      }
    }
  }, [destructiveDialog, t, hasUnsavedChanges, onBack, displayName, resetAttendees]);

  return transition(
    (style, item) =>
      item && (
        <BookingDetailsWrapper style={style}>
          <HaNModalHeader
            onBack={handleBackClick}
            onClose={handleClose}
            title={t("screen:MeetingRoomDetails")}
          />
          <MeetingRoomBookingDetailsContent
            attendees={attendees}
            dynamicTimeslot={dynamicTimeslot}
            isOnlineMeeting={isOnlineMeeting}
            message={message}
            onInviteesClick={() => setPage("addInvitees")}
            onIsOnlineMeetingChange={setIsOnlineMeeting}
            onMessageChange={setMessage}
            onTitleChange={setTitle}
            room={room}
            title={title}
          />
          <FlexCol paddingBottom={16}>
            <ModalPageBottom
              buttonLabel={t("meeting:CreateMeetingCreateEventButton")}
              buttonDisabled={!title || !startDateTime || !endDateTime}
              buttonLoading={isCreatingEvent}
              onClick={() =>
                createEvent(
                  {
                    title,
                    message,
                    attendees: attendees.map(attendee => attendee.email),
                    startDateTime,
                    endDateTime,
                    isOnlineMeeting,
                    rooms: [email],
                    isAllDay: false,
                    organizerTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    origin: "BookRoomNow",
                  },
                  t("meeting:CreateMeetingSuccessMessage", {
                    startDate: confirmationMessageStartDateTime,
                    endDate: confirmationMessageEndDateTime,
                  }),
                )
              }
            />
          </FlexCol>
        </BookingDetailsWrapper>
      ),
  );
};
